// /doctors — user's doctor directory. Per Marlee UAT 2026-05-20:
// "Section where they can add their doctors so the info is in one
// place: Name, Address, Phone, Specialty, etc."

import { useEffect, useState } from 'react';
import { ComplianceNotice } from '../components/ComplianceNotice';
import { SeniorBanner } from '../components/senior/SeniorBanner';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface Doctor {
  id: string;
  name: string;
  specialty: string | null;
  phone: string | null;
  address: string | null;
  email: string | null;
  notes: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  ok: boolean;
  data?: { items: Doctor[] };
  error?: { message: string };
}

interface SaveResponse {
  ok: boolean;
  data?: Doctor;
  error?: { message: string };
}

interface DraftDoctor {
  name: string;
  specialty: string;
  phone: string;
  address: string;
  email: string;
  notes: string;
  isPrimary: boolean;
}

const EMPTY_DRAFT: DraftDoctor = {
  name: '',
  specialty: '',
  phone: '',
  address: '',
  email: '',
  notes: '',
  isPrimary: false,
};

export function Doctors(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [doctors, setDoctors] = useState<Doctor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftDoctor>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/doctors');
        const body = (await res.json()) as ListResponse;
        if (cancelled) return;
        if (!body.ok) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
        setDoctors(body.data?.items ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  async function addDoctor() {
    if (!draft.name.trim()) {
      setError('Doctor name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authedFetch('/api/doctors', {
        method: 'POST',
        body: JSON.stringify({
          name: draft.name.trim(),
          specialty: draft.specialty.trim() || null,
          phone: draft.phone.trim() || null,
          address: draft.address.trim() || null,
          email: draft.email.trim() || null,
          notes: draft.notes.trim() || null,
          isPrimary: draft.isPrimary,
        }),
      });
      const body = (await res.json()) as SaveResponse;
      if (!body.ok || !body.data) throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      setDoctors((prev) => (prev ? [body.data as Doctor, ...prev] : [body.data as Doctor]));
      setDraft(EMPTY_DRAFT);
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function removeDoctor(id: string) {
    if (!window.confirm('Remove this doctor from your list?')) return;
    setError(null);
    try {
      const res = await authedFetch(`/api/doctors/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      }
      setDoctors((prev) => (prev ? prev.filter((d) => d.id !== id) : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppPage title="My doctors" back={{ to: '/account', label: 'Account' }}>
      <ComplianceNotice
        variant="clinical"
        detail="Your doctor list is private and stays inside MedReview. We attach it to the printable visit summary so you can hand the right name to each provider."
      />

      {error ? (
        <SeniorBanner title="Could not update your doctor list" tone="critical">
          {error}
        </SeniorBanner>
      ) : null}

      {doctors === null ? (
        <p style={{ padding: 'var(--space-md)' }}>Loading...</p>
      ) : doctors.length === 0 && !adding ? (
        <div className="empty-state">
          <h3 className="empty-state__title">No doctors saved yet</h3>
          <p className="empty-state__body">
            Add your primary care doctor, specialists, and pharmacy in one place. They show up on
            the printable visit summary too.
          </p>
          <SeniorButton variant="primary" onClick={() => setAdding(true)}>
            Add my first doctor
          </SeniorButton>
        </div>
      ) : (
        <>
          {doctors.length > 0 ? (
            <ul className="med-list" aria-label="My doctors">
              {doctors.map((d) => (
                <li key={d.id}>
                  <article className="med-card" style={{ flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <h3 className="med-card__title">
                          {d.name}
                          {d.isPrimary ? (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: '2px 8px',
                                background: 'var(--color-accent-soft)',
                                color: 'var(--color-accent)',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              Primary
                            </span>
                          ) : null}
                        </h3>
                        {d.specialty ? <p className="med-card__sub">{d.specialty}</p> : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => void removeDoctor(d.id)}
                        style={{
                          background: 'transparent',
                          border: 0,
                          color: 'var(--color-text-secondary)',
                          fontSize: 14,
                          cursor: 'pointer',
                          alignSelf: 'flex-start',
                        }}
                        aria-label={`Remove ${d.name}`}
                      >
                        Remove
                      </button>
                    </div>
                    <dl
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        columnGap: 12,
                        rowGap: 4,
                        fontSize: 15,
                        margin: 0,
                      }}
                    >
                      {d.phone ? (
                        <>
                          <dt style={{ color: 'var(--color-text-secondary)' }}>Phone</dt>
                          <dd style={{ margin: 0 }}>
                            <a href={`tel:${d.phone}`} style={{ color: 'var(--color-accent)' }}>
                              {d.phone}
                            </a>
                          </dd>
                        </>
                      ) : null}
                      {d.address ? (
                        <>
                          <dt style={{ color: 'var(--color-text-secondary)' }}>Address</dt>
                          <dd style={{ margin: 0 }}>{d.address}</dd>
                        </>
                      ) : null}
                      {d.email ? (
                        <>
                          <dt style={{ color: 'var(--color-text-secondary)' }}>Email</dt>
                          <dd style={{ margin: 0 }}>
                            <a href={`mailto:${d.email}`} style={{ color: 'var(--color-accent)' }}>
                              {d.email}
                            </a>
                          </dd>
                        </>
                      ) : null}
                      {d.notes ? (
                        <>
                          <dt style={{ color: 'var(--color-text-secondary)' }}>Notes</dt>
                          <dd style={{ margin: 0 }}>{d.notes}</dd>
                        </>
                      ) : null}
                    </dl>
                  </article>
                </li>
              ))}
            </ul>
          ) : null}

          {!adding ? (
            <SeniorButton onClick={() => setAdding(true)} fullWidth>
              Add another doctor
            </SeniorButton>
          ) : null}
        </>
      )}

      {adding ? (
        <section
          style={{
            background: 'var(--color-surface-primary)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            marginTop: 'var(--space-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-md)',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 20 }}>Add a doctor</h3>
          <DoctorField
            label="Name"
            required
            value={draft.name}
            onChange={(v) => setDraft({ ...draft, name: v })}
            placeholder="Dr. Jane Smith"
            autoFocus
          />
          <DoctorField
            label="Specialty"
            value={draft.specialty}
            onChange={(v) => setDraft({ ...draft, specialty: v })}
            placeholder="Primary care, Cardiology, Pharmacy, etc."
          />
          <DoctorField
            label="Phone"
            type="tel"
            value={draft.phone}
            onChange={(v) => setDraft({ ...draft, phone: v })}
            placeholder="(555) 123-4567"
          />
          <DoctorField
            label="Address"
            value={draft.address}
            onChange={(v) => setDraft({ ...draft, address: v })}
            placeholder="123 Main St, City, State"
          />
          <DoctorField
            label="Email"
            type="email"
            value={draft.email}
            onChange={(v) => setDraft({ ...draft, email: v })}
            placeholder="optional"
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Notes</span>
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={3}
              placeholder="Optional. Office hours, parking, anything to remember"
              style={{
                padding: '12px 14px',
                fontSize: 16,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                resize: 'vertical',
              }}
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-secondary)',
            }}
          >
            <input
              type="checkbox"
              checked={draft.isPrimary}
              onChange={(e) => setDraft({ ...draft, isPrimary: e.target.checked })}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ fontSize: 15 }}>Mark as my primary doctor</span>
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <SeniorButton
              variant="primary"
              onClick={() => void addDoctor()}
              disabled={saving || !draft.name.trim()}
            >
              {saving ? 'Saving...' : 'Save doctor'}
            </SeniorButton>
            <SeniorButton
              onClick={() => {
                setAdding(false);
                setDraft(EMPTY_DRAFT);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </SeniorButton>
          </div>
        </section>
      ) : null}
    </AppPage>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  type?: 'text' | 'tel' | 'email';
}

function DoctorField({
  label,
  value,
  onChange,
  placeholder,
  required,
  autoFocus,
  type = 'text',
}: FieldProps): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 16, fontWeight: 600 }}>
        {label}
        {required ? <span style={{ color: 'var(--color-danger)' }}> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        // biome-ignore lint/a11y/noAutofocus: senior UX -- the form is the page focus on open
        autoFocus={autoFocus}
        style={{
          padding: '12px 14px',
          fontSize: 18,
          minHeight: 48,
          border: '1.5px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
        }}
      />
    </label>
  );
}
